<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click="closeModal"
  >
    <div
      class="card-neumorphic max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      @click.stop
      v-motion
      :initial="{ opacity: 0, scale: 0.8 }"
      :enter="{ opacity: 1, scale: 1 }"
      :leave="{ opacity: 0, scale: 0.8 }"
      :duration="300"
    >
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-2xl font-poppins font-semibold text-dark-gray">
          {{ serviceDetails.title }}
        </h3>
        <button
          @click="closeModal"
          class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
        >
          <X :size="20" class="text-dark-gray" />
        </button>
      </div>

      <!-- Content -->
      <div class="space-y-6">
        <!-- Icon and Description -->
        <div class="text-center">
          <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-neumorphic flex items-center justify-center shadow-soft">
            <component :is="serviceDetails.icon" :size="40" class="text-light-purple" />
          </div>
          <p class="text-lg text-dark-gray/80 leading-relaxed">
            {{ serviceDetails.description }}
          </p>
        </div>

        <!-- Features -->
        <div>
          <h4 class="text-xl font-poppins font-semibold text-dark-gray mb-4">
            What's Included:
          </h4>
          <div class="grid md:grid-cols-2 gap-3">
            <div
              v-for="(feature, index) in serviceDetails.features"
              :key="index"
              class="flex items-center space-x-3 p-3 rounded-lg bg-light-purple/5"
            >
              <CheckCircle :size="20" class="text-light-purple flex-shrink-0" />
              <span class="text-dark-gray/80">{{ feature }}</span>
            </div>
          </div>
        </div>

        <!-- Process -->
        <div>
          <h4 class="text-xl font-poppins font-semibold text-dark-gray mb-4">
            How It Works:
          </h4>
          <div class="space-y-3">
            <div
              v-for="(step, index) in serviceDetails.process"
              :key="index"
              class="flex items-start space-x-4 p-4 rounded-lg bg-gradient-to-r from-light-purple/5 to-transparent"
            >
              <div class="w-8 h-8 rounded-full bg-light-purple text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                {{ index + 1 }}
              </div>
              <div>
                <h5 class="font-semibold text-dark-gray mb-1">{{ step.title }}</h5>
                <p class="text-dark-gray/70 text-sm">{{ step.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Benefits -->
        <div>
          <h4 class="text-xl font-poppins font-semibold text-dark-gray mb-4">
            Why Choose Our Service:
          </h4>
          <div class="grid md:grid-cols-2 gap-4">
            <div
              v-for="(benefit, index) in serviceDetails.benefits"
              :key="index"
              class="flex items-start space-x-3"
            >
              <Star :size="16" class="text-light-purple mt-1 flex-shrink-0" />
              <span class="text-dark-gray/80 text-sm">{{ benefit }}</span>
            </div>
          </div>
        </div>

        <!-- CTA -->
        <div class="text-center pt-4 border-t border-gray-200">
          <p class="text-dark-gray/70 mb-4">Ready to get started?</p>
          <button
            @click="requestService"
            class="btn-neumorphic text-light-purple hover:text-white hover:bg-light-purple relative overflow-hidden"
          >
            <span class="relative z-10">Request This Service</span>
            <div
              class="absolute inset-0 bg-light-purple transition-all duration-300 opacity-0 hover:opacity-100"
              style="border-radius: 24px"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { X, CheckCircle, Star, Building2, Home } from 'lucide-vue-next'

export default {
  name: 'LearnMoreModal',
  components: {
    X,
    CheckCircle,
    Star,
    Building2,
    Home
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    serviceType: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'request-service'],
  setup(props, { emit }) {
    const serviceDetails = computed(() => {
      if (props.serviceType === 'corporate') {
        return {
          title: 'Corporate Medical Camps',
          icon: Building2,
          description: 'Comprehensive on-site healthcare services designed to promote employee wellness and ensure a healthy workforce. Our professional medical teams bring advanced diagnostic capabilities directly to your workplace.',
          features: [
            'Complete health screenings',
            'Blood tests and diagnostics',
            'BMI and vital signs check',
            'Eye and hearing tests',
            'Cardiovascular assessments',
            'Diabetes screening',
            'Detailed health reports',
            'Follow-up consultations'
          ],
          process: [
            {
              title: 'Initial Consultation',
              description: 'We discuss your company\'s specific health screening requirements and customize our services accordingly.'
            },
            {
              title: 'Schedule & Setup',
              description: 'Our team coordinates with your HR department to schedule the camp and set up all necessary equipment on-site.'
            },
            {
              title: 'Health Screening',
              description: 'Certified medical professionals conduct comprehensive health checks for all participating employees.'
            },
            {
              title: 'Reports & Analysis',
              description: 'Individual and aggregate health reports are provided with recommendations for follow-up care.'
            }
          ],
          benefits: [
            'Improved employee health and productivity',
            'Reduced healthcare costs',
            'Early detection of health issues',
            'Convenient on-site service',
            'Professional medical team',
            'Comprehensive health data',
            'Customizable screening packages',
            'Follow-up support available'
          ]
        }
      } else {
        return {
          title: 'Home Blood Collection',
          icon: Home,
          description: 'Professional blood collection services at your doorstep. Our certified phlebotomists ensure safe, hygienic, and comfortable sample collection in the comfort of your home.',
          features: [
            'Certified phlebotomists',
            'Sterile equipment and procedures',
            'Wide range of blood tests',
            'Convenient home service',
            'Quick sample processing',
            'Digital report delivery',
            'Doctor consultations available',
            'Emergency sample collection'
          ],
          process: [
            {
              title: 'Book Appointment',
              description: 'Schedule your preferred time slot through our easy booking system or customer service.'
            },
            {
              title: 'Preparation',
              description: 'Receive pre-test instructions and prepare for the sample collection as advised.'
            },
            {
              title: 'Sample Collection',
              description: 'Our certified phlebotomist visits your home and collects samples using sterile procedures.'
            },
            {
              title: 'Results & Reports',
              description: 'Receive your test results digitally and consult with our doctors if needed.'
            }
          ],
          benefits: [
            'No travel or waiting time',
            'Safe and hygienic procedures',
            'Experienced medical professionals',
            'Flexible scheduling options',
            'Competitive pricing',
            'Quick turnaround time',
            'Digital report access',
            'Doctor consultation available'
          ]
        }
      }
    })

    const closeModal = () => {
      emit('close')
    }

    const requestService = () => {
      const serviceType = props.serviceType === 'corporate' ? 'corporate-camp' : 'home-collection'
      emit('request-service', serviceType)
      closeModal()
    }

    return {
      serviceDetails,
      closeModal,
      requestService
    }
  }
}
</script>
